import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const capabilities = [
    {
      title: "Frontend Development",
      description:
        "Building responsive and interactive user interfaces using React, Angular, HTML5, CSS3, Bootstrap, and JavaScript.",
    },
    {
      title: "Mobile Development",
      description:
        "Developing cross-platform mobile applications with Flutter and React Native, focusing on performance, usability, and real-time features.",
    },
    {
      title: "Backend Development",
      description:
        "Creating secure and scalable APIs using Node.js, Express.js, Laravel, Spring Boot, and .NET Web API.",
    },
    {
      title: "Database Management",
      description:
        "Working with MySQL, MongoDB, MSSQL, Oracle, and SQLite to design efficient data models and optimize queries.",
    },
    {
      title: "DevOps & Cloud",
      description:
        "Using Git, Docker, Kubernetes, Firebase, and Azure to streamline development workflows and deployment processes.",
    },
  ];

  const experiences = [
    {
      role: "Associate Full Stack Developer",
      company: "Weblook International Pvt Ltd",
      period: "2026 - Present",
      details: [
        "Design and develop scalable web and mobile applications using Flutter, Laravel, and MySQL.",
        "Build and integrate RESTful APIs for frontend and mobile platforms.",
        "Collaborate with agile teams on requirement analysis, development, testing, and deployment.",
        "Write clean, maintainable, and reusable code following industry best practices.",
      ],
    },
    {
      role: "Full Stack Developer Intern",
      company: "Weblook International Pvt Ltd",
      period: "2025 - 2026",
      details: [
        "Completed a one-year full-time internship focused on Android and iOS mobile development.",
        "Developed Flutter applications integrated with Laravel backends.",
        "Worked with Firebase for authentication, real-time databases, and cloud storage.",
        "Assisted in API development and integration for web and mobile applications.",
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-12 px-6 md:px-12 lg:px-16 overflow-hidden">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-10"
      >
        <motion.div variants={itemVariants} className="flex-1">
          <motion.h2
            variants={itemVariants}
            className="text-5xl md:text-5xl font-mono font-bold text-gray-900 mb-8 relative "
          >
            About Me
            
          </motion.h2>

          <motion.div
            variants={containerVariants}
            className="text-md leading-relaxed  text-gray-700 space-y-8"
          >
            <motion.div
              variants={itemVariants}
              className="space-y-4 text-left text-justify hover:text-gray-900 transition-colors duration-300"
            >
              <p>
                I am <strong>Tharindu Herath</strong>, a dedicated and
                self-motivated Software Engineer with a strong passion for
                building modern digital solutions that create real-world impact.
                I specialize in full-stack web and mobile application
                development, with hands-on experience in technologies such as
                Flutter, React, Node.js, Laravel, .NET, and MongoDB. Over the
                past few years, I have worked on a wide range of projects
                including ERP systems, e-commerce platforms, AI-powered
                applications, and enterprise web solutions.
              </p>
              <p>
                Currently, I work as an Associate Full Stack Developer at
                Weblook International Pvt Ltd, where I design, develop, and
                maintain scalable applications for both web and mobile platforms.
                My role involves building RESTful APIs, integrating cloud
                services, optimizing databases, and collaborating closely with
                agile development teams to deliver high-quality software
                solutions on time.
              </p>
              <p>
                I hold a BSc (Hons) in Information Technology specializing in
                Software Engineering from the Sri Lanka Institute of Information
                Technology (SLIIT). During my academic journey, I gained a
                strong foundation in software architecture, algorithms,
                databases, and system design while also leading and contributing
                to multiple practical projects.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-2xl font-mono font-bold text-gray-900 text-justify">
                Core Skills
              </h3>
              <div className="space-y-3">
                {capabilities.map((item, index) => (
                  <motion.div
                    key={item.title}
                    variants={itemVariants}
                    className="flex gap-3 hover:text-gray-900 transition-colors duration-300 text-sm text-justify"
                  >
                    <span className="font-mono font-bold text-gray-900 text-sm">
                      {index + 1}.
                    </span>
                    <p>
                      <strong>{item.title}</strong> - {item.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            
          </motion.div>

          <motion.a
            variants={itemVariants}
            href="/Tharindu%20herath.pdf"
            download
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block mt-8 px-8 py-3 border-2 border-gray-900 text-gray-900 
            font-mono text-lg rounded-lg transition-all duration-300 
            hover:bg-gray-900 hover:text-white hover:shadow-xl"
          >
            Download CV
          </motion.a>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex-1 flex flex-col  md:items-end gap-4"
        >
          <div className="relative">
            <motion.img
              initial={{ opacity: 0, scale: 1 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              src="new2.png"
              alt="Tharindu Herath"
              className="w-[480px] h-[450px] object-cover rounded-xl shadow-2xl relative z-10"
            />
            <motion.div
              initial={{ opacity: 0, x: 0, y: 0 }}
              animate={isInView ? { opacity: 0.8, x: -24, y: -24 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="absolute top-0 right-0 w-[380px] h-[420px] bg-gray-900 rounded-xl -z-10"
            ></motion.div>
          </div>

          <motion.div
            variants={itemVariants}
            className="w-full max-w-[520px] space-y-5 text-lg leading-relaxed text-gray-700"
          >
            <h3 className="text-2xl font-mono font-bold text-gray-900 text-justify">
              Professional Experience
            </h3>
            {experiences.map((experience) => (
              <motion.div
                key={`${experience.role}-${experience.period}`}
                variants={itemVariants}
                className="space-y-3 hover:text-gray-900 transition-colors duration-300 text-justify"
              >
                <h4 className="font-mono text-sm font-bold text-gray-900">
                  {experience.role} - {experience.company} ({experience.period})
                </h4>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  {experience.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About;
